"use client";
/**
 * The open line's controls (spec 2026-09-12 §5, ruling 8: "the tray should be
 * beautiful, no copper color, just 3 things: timer on left, keyboard button
 * on center, and end button on right"). A porcelain pill of the seat's own
 * material in the row beneath the ball: the timer in the app's mono, two round
 * porcelain buttons in one quiet ink, their words only as captions on hover.
 * The stage raises it after the ready beat and sinks it when the line
 * closes (data-up; the rules live in globals.css, the cl- stage section).
 * Two seats came back since, each only where it is wired: the mute
 * (2026-09-19) and attach (2026-09-24, a line that takes files), so the
 * order is clock · mute · attach · type · end.
 */
import { mmss } from "./lineMachine";

export function KeyboardGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <path d="M7 10h.01M11 10h.01M15 10h.01M17 10h.01M7 14h10" />
    </svg>
  );
}

/** The paperclip: attach, the typing box's other door (a "+" beside the end
 *  "×" would read as the same glyph turned). */
function ClipGlyph({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m16 6-8.41 8.59a2 2 0 0 0 2.83 2.83l8.41-8.59a4 4 0 0 0-5.66-5.66l-8.38 8.55a6 6 0 0 0 8.49 8.49l8.38-8.55" />
    </svg>
  );
}

/** The mic, with its slash while the line is muted (the glyph of the stage's
 *  old mute control, back in the tray by the founder's ask 2026-09-19). */
function MicGlyph({ size, muted }: { size: number; muted: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      {muted && <path d="M4 4l16 16" />}
    </svg>
  );
}

export interface LineTrayProps {
  /** Up in the row (after the ready beat); down while it sinks. */
  up: boolean;
  seconds: number;
  onType: () => void;
  onEnd: () => void;
  /** The mic is wired: the mute takes its seat between the clock and type.
   *  Without it the tray is the three of ruling 8, untouched. */
  onMute?: (muted: boolean) => void;
  muted?: boolean;
  /** The line takes files (attach on the call, 2026-09-24): the attach seat
   *  sits between the mute and type. Without it the tray is today's. */
  onAttach?: () => void;
  /** How many files wait on the card: the seat's small count while any do. */
  attached?: number;
}

export function LineTray({
  up,
  seconds,
  onType,
  onEnd,
  onMute,
  muted = false,
  onAttach,
  attached = 0,
}: LineTrayProps) {
  return (
    <div
      className="cl-tray"
      data-up={up ? "" : undefined}
      data-mute={onMute ? "" : undefined}
      data-attach={onAttach ? "" : undefined}
      role="group"
      aria-label="Line controls"
    >
      <span className="cl-tray__clock">{mmss(seconds)}</span>
      {onMute && (
        <button
          type="button"
          className="cl-tray__btn cl-tray__btn--mute"
          data-cap={muted ? "unmute" : "mute"}
          title={muted ? "Unmute" : "Mute"}
          aria-label={muted ? "Unmute" : "Mute"}
          aria-pressed={muted}
          onClick={() => onMute(!muted)}
        >
          <MicGlyph size={15} muted={muted} />
        </button>
      )}
      {onAttach && (
        <button
          type="button"
          className="cl-tray__btn cl-tray__btn--attach"
          data-cap="attach"
          title="Attach"
          aria-label={
            attached > 0 ? `Attach files, ${attached} waiting` : "Attach files"
          }
          onClick={onAttach}
        >
          <ClipGlyph size={15} />
          {attached > 0 && (
            <span
              className="pointer-events-none absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-fg-default px-1 text-2.5 leading-none font-semibold text-fg-inverse tabular-nums"
              aria-hidden
            >
              {attached}
            </span>
          )}
        </button>
      )}
      <button
        type="button"
        className="cl-tray__btn cl-tray__btn--type"
        data-cap="type"
        title="Type"
        aria-label="Type"
        onClick={onType}
      >
        <KeyboardGlyph size={15} />
      </button>
      <button
        type="button"
        className="cl-tray__btn cl-tray__btn--end"
        data-cap="end"
        title="End"
        aria-label="End"
        onClick={onEnd}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}
